// Copyright 2017 Sorama B.V.

import { ArrayExtensions } from "extensions/ArrayExtensions";
import { Generic } from "units/Generic";

/* List of Octave-Band and One-Third Octave-Band center-frequency definitions
 * From the book: "Fundamentals of Noise and Vibration Analysis for Engineers, Second Edition, p.264
 */
const fRef = 1000; // [Hz]

//Was listed as Math.pow(10, 3 / 10) in the book, but we use 2 instead
const octaveScaleFactor = 2;
const octaveStartBand = -5;
const octaveBands = 10;
const OctaveBandCenterFrequencies: number[] = new Array(octaveBands)
    .fill(null)
    .map((x, idx) => fRef * Math.pow(octaveScaleFactor, octaveStartBand + idx));

//Was listed as Math.pow(10, 1 / 10) in the book, but we use 1/3rd of 2 instead
const tertzScaleFactor = Math.pow(2, 1 / 3);
const tertzStartBand = -16;
const tertzBands = 30;
const OneThirdOctaveBandCenterFrequencies: number[] = new Array(tertzBands)
    .fill(null)
    .map((x, idx) => fRef * Math.pow(tertzScaleFactor, tertzStartBand + idx));

interface IFrequencyBand {
    readonly center: number;
    readonly lower: number;
    readonly upper: number;
}

export class BandValue {
    public readonly band: IFrequencyBand;
    public readonly value: number;

    constructor(frequencyBand: IFrequencyBand, value: number) {
        this.band = frequencyBand;
        this.value = value;
    }
}

export class LinearFrequencyBand implements IFrequencyBand {
    public readonly center: number;
    public readonly lower: number;
    public readonly upper: number;

    constructor(center: number, deltaf: number) {
        this.center = center;
        this.lower = center - deltaf / 2;
        this.upper = center + deltaf / 2;
    }
}

abstract class LogarithmicFrequencyBand implements IFrequencyBand {
    public readonly center: number;
    private scaleFactor: number;

    public get lower(): number {
        return this.center / Math.sqrt(this.scaleFactor);
    }

    public get upper(): number {
        return this.center * Math.sqrt(this.scaleFactor);
    }

    constructor(center: number, scaleFactor: number) {
        this.center = center;
        this.scaleFactor = scaleFactor;
    }
}

export class OctaveBand extends LogarithmicFrequencyBand {
    constructor(center: number) {
        super(center, octaveScaleFactor);
    }
}

export class OneThirdOctaveBand extends LogarithmicFrequencyBand {
    constructor(center: number) {
        super(center, tertzScaleFactor);
    }
}

// See: http://i.imgur.com/lcgQu.jpg
export class OctaveBands {
    public static OctaveBandFrequencies: OctaveBand[] = OctaveBandCenterFrequencies.map((x) => new OctaveBand(x));
    public static OneThirdOctaveBandFrequencies: OctaveBand[] = OneThirdOctaveBandCenterFrequencies.map(
        (x) => new OneThirdOctaveBand(x)
    );

    public static toOctaveBands(frequencyBandValues: BandValue[]): BandValue[] {
        return OctaveBands.toBands(frequencyBandValues, OctaveBands.OctaveBandFrequencies);
    }

    public static toOneThirdOctaveBands(frequencyBandValues: BandValue[]): BandValue[] {
        return OctaveBands.toBands(frequencyBandValues, OctaveBands.OneThirdOctaveBandFrequencies);
    }

    private static toBands(
        frequencyBandValues: BandValue[],
        octaveBandFrequencies: LogarithmicFrequencyBand[]
    ): BandValue[] {
        const octaveBands: BandValue[] = [];
        for (let i = 0; i < octaveBandFrequencies.length; i++) {
            const octaveBand = octaveBandFrequencies[i];

            // Get the frequency band range overlapping with this octave band
            const indices = ArrayExtensions.findFirstAndLastIndex(
                frequencyBandValues,
                (x) => x.band.upper >= octaveBand.upper,
                (x) => x.band.lower <= octaveBand.lower
            );

            // indices.lastIndex is the last occurence of frequencyBand.lower <= octaveBand.lower, e.g. the first frequency band overlapping this octave band
            let startFrequencyBandIdx = indices.lastIndex;

            // indices.firstIndex is the first occurence of frequencyBand.upper >= octaveBand.upper, e.g. the last frequency band overlapping this octave band
            let endFrequencyBandIdx = indices.firstIndex;

            // No single input band overlaps with this octave band
            if (startFrequencyBandIdx == -1 && endFrequencyBandIdx == -1) {
                octaveBands.push(new BandValue(octaveBand, 0));
                continue;
            }

            // Make sure we don't go out of bounds
            if (startFrequencyBandIdx == -1) startFrequencyBandIdx = 0;

            if (endFrequencyBandIdx == -1) endFrequencyBandIdx = frequencyBandValues.length - 1;

            let fullOverlapOctaveBandValue = 0;
            // Calculate squared summed value of frequency bands being fully overlaped by this octave band
            if (endFrequencyBandIdx - startFrequencyBandIdx > 1)
                fullOverlapOctaveBandValue = frequencyBandValues
                    .slice(startFrequencyBandIdx + 1, endFrequencyBandIdx)
                    .reduce((sum, fband) => sum + fband.value * fband.value, 0);

            // Calculate overlapping section of first overlapping frequency band
            const startFrequencyBand = frequencyBandValues[startFrequencyBandIdx];
            const startDeltaF = startFrequencyBand.band.upper - startFrequencyBand.band.lower;

            const startOverlapSectionStart = Math.max(octaveBand.lower, startFrequencyBand.band.lower); // Check if the frequency band starts after the octaveBand starts
            const startOverlapSectionEnd = Math.min(startFrequencyBand.band.upper, octaveBand.upper); // Check if the frequency band ends before the octaveBand ends
            const startOverlap = Math.max((startOverlapSectionEnd - startOverlapSectionStart) / startDeltaF, 0);

            const startOverlapOctaveBandValue = startOverlap * startFrequencyBand.value * startFrequencyBand.value;

            // Calculate overlapping section of last overlapping frequency band
            const endFrequencyBand = frequencyBandValues[endFrequencyBandIdx];
            const endDeltaF = endFrequencyBand.band.upper - endFrequencyBand.band.lower;

            const endOverlapSectionStart = Math.max(endFrequencyBand.band.lower, octaveBand.lower); // Check if the frequency band starts before the octaveBand starts
            const endOverlapSectionEnd = Math.min(octaveBand.upper, endFrequencyBand.band.upper); // Check if the frequency band ends before the octaveBand ends
            const endOverlap = Math.max((endOverlapSectionEnd - endOverlapSectionStart) / endDeltaF, 0);

            // Check if the start and end frequency bands aren't equal (can happen if the frequency bands are wider then this octave band)
            const endOverlapOctaveBandValue =
                startFrequencyBandIdx != endFrequencyBandIdx
                    ? endOverlap * endFrequencyBand.value * endFrequencyBand.value
                    : 0;

            octaveBands.push(
                new BandValue(
                    octaveBand,
                    Math.sqrt(startOverlapOctaveBandValue + fullOverlapOctaveBandValue + endOverlapOctaveBandValue)
                )
            );
        }

        return octaveBands;
    }

    public static toHumanReadableBandFrequency(frequency: number): string {
        const frequencyString = frequency.toFixed(0);

        const fixedDigits = frequency.toFixed(0).length;
        const roundingFactor = Math.pow(10, 3 - fixedDigits);

        // Round the 3rd digit to the nearest 5 execpt for bins starting with 7 because of rounding issues for those bins
        const roundTo = frequencyString[0] == "7" ? 10 : 5;
        const roundedDisplayValue = (Math.round((frequency * roundingFactor) / 5) * 5) / roundingFactor;

        const readableUnitValue = Generic.unitless.toHumanReadableUnit(roundedDisplayValue);
        const readableUnitValueString = readableUnitValue.value.toFixed(1);
        const showFractionalDigits =
            readableUnitValueString[readableUnitValueString.length - 1] == "0" ||
            readableUnitValueString.split(".")[0].length > 1
                ? 0
                : 1;
        return readableUnitValue.value.toFixed(showFractionalDigits).toString() + readableUnitValue.unit.unit;
    }
}
